const DISCORD_CLIENT_ID = '1352491934383472792';
const REDIRECT_URI = `${window.location.origin}/auth-callback.html`;
const DISCORD_AUTH_URL = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_type=token&scope=identify`;

const GOOGLE_CLIENT_ID = '526650557926-6863u14gsko0fogeilbe1sttsd4vqhd5.apps.googleusercontent.com';

export interface DiscordUser {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  global_name: string | null;
  authProvider?: 'discord' | 'google';
}

export function getUser(): DiscordUser | null {
  const stored = localStorage.getItem('discord_user');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as DiscordUser;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem('discord_token');
}

export function getJWT(): string | null {
  return localStorage.getItem('jwt_token');
}

export function isLoggedIn(): boolean {
  return !!getUser() && !!getJWT();
}

export function authHeaders(): Record<string, string> {
  const token = getJWT();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function login(): void {
  localStorage.setItem('discord_redirect_back', window.location.href);
  window.location.href = '/login';
}

export function loginWithDiscord(): void {
  window.location.href = DISCORD_AUTH_URL;
}

export function logout(): void {
  localStorage.removeItem('discord_user');
  localStorage.removeItem('discord_token');
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('discord_redirect_back');
  window.location.reload();
}

export function getAvatarUrl(user: DiscordUser): string {
  if (user.authProvider === 'google' && user.avatar) {
    return user.avatar;
  }
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`;
  }
  if (user.authProvider === 'google') {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(user.global_name || user.username)}&background=C9A96E&color=1A1714&size=64`;
  }
  const defaultIndex = (BigInt(user.id) >> 22n) % 6n;
  return `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png`;
}

export function getDisplayName(user: DiscordUser): string {
  return user.global_name || user.username;
}

export async function handleCallback(): Promise<string | null> {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  const discordAccessToken = params.get('access_token');

  if (!discordAccessToken) return null;

  try {
    // Exchange Discord token for a server-issued JWT
    const response = await fetch('/api/auth/discord', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: discordAccessToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    localStorage.setItem('jwt_token', data.token);
    localStorage.setItem('discord_token', discordAccessToken);
    localStorage.setItem('discord_user', JSON.stringify({ ...data.user, authProvider: 'discord' }));

    const redirectBack = localStorage.getItem('discord_redirect_back') || '/';
    localStorage.removeItem('discord_redirect_back');
    return redirectBack;
  } catch {
    return null;
  }
}

export async function handleGoogleCallback(idToken: string): Promise<string | null> {
  try {
    const response = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    localStorage.setItem('jwt_token', data.token);
    localStorage.setItem('discord_user', JSON.stringify({ ...data.user, authProvider: 'google' }));

    const redirectBack = localStorage.getItem('discord_redirect_back') || window.location.href;
    localStorage.removeItem('discord_redirect_back');
    return redirectBack;
  } catch {
    return null;
  }
}

export function initGoogleSignIn(buttonElement?: HTMLElement | null): void {
  const google = (window as any).google;
  if (!google?.accounts?.id) return;

  google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    ux_mode: 'popup',
    use_fedcm_for_prompt: false,
    callback: async (response: any) => {
      const redirectUrl = await handleGoogleCallback(response.credential);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    },
  });

  if (buttonElement) {
    const btnWidth = Math.min(buttonElement.offsetWidth || 400, 400);
    google.accounts.id.renderButton(buttonElement, {
      type: 'standard',
      theme: 'filled_black',
      size: 'large',
      text: 'signin_with',
      shape: 'pill',
      width: btnWidth,
    });
  }
}

// Auto-refresh JWT before it expires
export function startTokenRefresh(): void {
  if (!isLoggedIn()) return;

  // Check token expiry every 5 minutes
  setInterval(async () => {
    const token = getJWT();
    if (!token) return;

    try {
      // Decode JWT payload to check expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const timeLeft = expiresAt - Date.now();

      // Refresh if less than 1 day remaining
      if (timeLeft < 24 * 60 * 60 * 1000 && timeLeft > 0) {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('jwt_token', data.token);
        }
      }

      // If token is expired, force logout
      if (timeLeft <= 0) {
        logout();
      }
    } catch {
      // Token decode failed — ignore
    }
  }, 5 * 60 * 1000);
}

export function initAuthUI(): void {
  const user = getUser();
  
  // Try new global navigation first
  const navCta = document.querySelector('.nav-cta');
  
  // Fallback to old navigation
  const navLinks = document.querySelector('.nav-links');
  const existingGetStarted = navLinks?.querySelector('.btn-primary');

  if (user) {
    const userEl = document.createElement('div');
    userEl.className = 'nav-user-wrapper';
    userEl.innerHTML = `
      <button class="nav-user-btn" id="navUserBtn">
        <img src="${getAvatarUrl(user)}" alt="Avatar" class="nav-avatar" />
        <span class="nav-username">${getDisplayName(user)}</span>
        <svg class="nav-user-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      <div class="nav-user-dropdown" id="navUserDropdown">
        <div class="nav-dropdown-header">
          <img src="${getAvatarUrl(user)}" alt="Avatar" class="nav-dropdown-avatar" />
          <div class="nav-dropdown-info">
            <div class="nav-dropdown-name">${getDisplayName(user)}</div>
            <div class="nav-dropdown-email">${user.username}</div>
          </div>
        </div>
        <div class="nav-dropdown-divider"></div>
        <a href="/orders" class="nav-dropdown-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          My Orders
        </a>
        <a href="/updates" class="nav-dropdown-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Updates
        </a>
        <a href="/help-center" class="nav-dropdown-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Help Center
        </a>
        <div class="nav-dropdown-divider"></div>
        <button class="nav-dropdown-item nav-dropdown-logout" id="logoutBtn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign Out
        </button>
      </div>
    `;
    
    // Replace in new navigation
    if (navCta) {
      navCta.replaceWith(userEl);
    }
    // Replace in old navigation
    else if (existingGetStarted) {
      existingGetStarted.replaceWith(userEl);
    } else if (navLinks) {
      navLinks.appendChild(userEl);
    }

    // Toggle dropdown
    const userBtn = document.getElementById('navUserBtn');
    const dropdown = document.getElementById('navUserDropdown');
    
    userBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown?.classList.toggle('active');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!userEl.contains(e.target as Node)) {
        dropdown?.classList.remove('active');
      }
    });

    document.getElementById('logoutBtn')?.addEventListener('click', logout);
  } else {
    // Not logged in - ensure sign in button exists
    if (existingGetStarted && navLinks) {
      const signInLink = document.createElement('a');
      signInLink.href = '/login';
      signInLink.className = 'btn-primary';
      signInLink.style.textDecoration = 'none';
      signInLink.textContent = 'Sign In';
      signInLink.addEventListener('click', () => {
        localStorage.setItem('discord_redirect_back', window.location.href);
      });
      existingGetStarted.replaceWith(signInLink);
    }
  }
}

export function waitForGoogleScript(callback: () => void): void {
  if ((window as any).google?.accounts?.id) {
    callback();
    return;
  }
  // Poll until the script loads (max 5 seconds)
  let attempts = 0;
  const interval = setInterval(() => {
    attempts++;
    if ((window as any).google?.accounts?.id) {
      clearInterval(interval);
      callback();
    } else if (attempts > 50) {
      clearInterval(interval);
    }
  }, 100);
}
